import { DatePicker } from "antd";
import { useState } from "react";
import { useForm } from "react-hook-form";
import useAxios from "../../../../MainComponents/Hooks/useAxios";
import { Spin } from "antd";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const { RangePicker } = DatePicker;

export default function TopResults() {
  const [date, setDate] = useState([]);
  const [filterStage, setFilterStage] = useState(null); // Store selected stage filter
  const [submitAdd, setSubmitAdd] = useState({
    flag: "",
    depdency: false,
    data: {},
  });

  const [successData, errorData, loadingSuccess] = useAxios(
    process.env.REACT_APP_ANALYTICS_TOP_RESULTS_QUESTIONS_API,
    "POST",
    submitAdd.flag,
    submitAdd.depdency,
    submitAdd.data,
    true
  );

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm();

  const handleDateChange = (dates, dateString) => {
    if (dates) {
      const formattedDates = dates.map((date) => date.format("YYYY-MM-DD")); // Format to "YYYY-MM-DD"
      setDate(formattedDates);
      setValue("dateRange", formattedDates); // Set for validation
    }
  };

  const onSubmit = (data) => {
    if (!date.length) {
      toast.error("Please select a valid date range.");
      return;
    }

    const [from, to] = date;

    setSubmitAdd({
      flag: "Change Table",
      depdency: !submitAdd.depdency,
      data: {
        from,
        to,
        limit: data.limit,
      },
    });
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Top Results Students Report", 20, 20);
    let yOffset = 30; // Initial vertical offset
    successData?.data.forEach((student) => {
      // Check if the content for the student will fit on the current page
      if (yOffset + 40 > doc.internal.pageSize.height) {
        doc.addPage(); // Add a new page if content exceeds the page
        yOffset = 20; // Reset yOffset for new page
      }
      // Add student details to PDF
      doc.setFontSize(12);
      doc.text(`Student Name: ${student.student_name}`, 20, yOffset);
      yOffset += 10;
      doc.text(`Student Degree: ${student.student_degree}`, 20, yOffset);
      yOffset += 10;
      doc.text(`Mobile: ${student.mobile}`, 20, yOffset);
      yOffset += 10;
      doc.text(`Father's Mobile: ${student.parent_mobile}`, 20, yOffset);
      yOffset += 30; // Add 30px gap after each student's details
    });
    doc.save("Top_students_report.pdf"); // Download the generated PDF
  };

  // Filter data by stage
  const filteredData = filterStage
    ? successData?.data.filter((student) => student.stage === filterStage)
    : successData?.data;

  return (
    <div className="px-10 py-10 flex flex-col gap-5 min-h-screen">
      {/* Form Section */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-1/2 sm:w-full space-y-5 rounded-3xl border border-secondary shadow-2xl p-5"
      >
        <RangePicker
          className="signin-inputs"
          onChange={handleDateChange}
          getPopupContainer={(trigger) => trigger.parentNode}
        />
        {errors.dateRange && (
          <p className="text-red-500">{errors.dateRange.message}</p>
        )}

        <input
          type="number"
          min={1}
          max={1000}
          className="signin-inputs"
          placeholder="Limit of students"
          {...register("limit", {
            required: "Limit is required",
            min: { value: 1, message: "Minimum limit is 1" },
            max: { value: 1000, message: "Maximum limit is 1000" },
          })}
        />
        {errors.limit && <p className="text-red-500">{errors.limit.message}</p>}

        <button
          type="submit"
          className="submit"
          disabled={loadingSuccess || !isValid}
        >
          {loadingSuccess ? <Spin /> : "Submit"}
        </button>
      </form>

      {/* Stage Filter Buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => setFilterStage(1)}
          className={`px-4 py-2 rounded-3xl duration-300 ${
            filterStage === 1
              ? "bg-secondary text-white"
              : "border border-secondary text-secondary"
          }`}
        >
          1st sec
        </button>
        <button
          onClick={() => setFilterStage(2)}
          className={`px-4 py-2 rounded-3xl duration-300 ${
            filterStage === 2
              ? "bg-secondary text-white"
              : "border border-secondary text-secondary"
          }`}
        >
          2nd sec
        </button>
        <button
          onClick={() => setFilterStage(3)}
          className={`px-4 py-2 rounded-3xl duration-300 ${
            filterStage === 3
              ? "bg-secondary text-white"
              : "border border-secondary text-secondary"
          }`}
        >
          3rd sec
        </button>
        <button
          onClick={() => setFilterStage(null)}
          className={`px-4 py-2 rounded-3xl duration-300 ${
            filterStage === null
              ? "bg-secondary text-white"
              : "border border-secondary text-secondary"
          }`}
        >
          All Stages
        </button>
      </div>

      {/* PDF Export Button */}
      {successData && successData.data.length > 0 && (
        <button
          onClick={exportPDF}
          className="w-[200px] rounded-lg border-2 border-delete bg-delete px-2 py-1 font-semibold text-bg transition-all duration-300 hover:bg-bg hover:text-delete active:scale-90"
        >
          Print PDF
          <FontAwesomeIcon className="ml-2" icon={faDownload} />
        </button>
      )}

      {/* Student Cards */}
      <div className="flex gap-5 flex-wrap" id="export-pdf">
        {loadingSuccess
          ? null
          : filteredData &&
            filteredData.map((student) => (
              <div
                key={student.id}
                className="w-full p-4 rounded-2xl shadow-lg bg-white relative"
              >
                <div className="flex flex-col gap-3 items-center">
                  <img
                    src={student.profile_pic}
                    alt={student.student_name}
                    className="w-32 h-32 rounded-full"
                  />
                  <p className="text-xl font-bold">{student.student_name}</p>
                  <p className="text-emerald-500 text-lg">
                    Student Degree: {student.student_degree}
                  </p>
                  <div className="absolute top-5 left-5 bg-secondary rounded-3xl px-4 py-3 border-secondary border text-white">
                    <p className="text-md">Stage: {student.stage}</p>
                  </div>
                  <p className="text-gray-700">Mobile: {student.mobile}</p>
                  <p className="text-gray-700">
                    Father's Mobile: {student.parent_mobile}
                  </p>
                </div>
              </div>
            ))}
      </div>
    </div>
  );
}
